import { useMemo } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

// import { PATHS } from 'src/routes/paths';

import { useAuthContext } from 'src/auth/hooks';

import { varHover } from 'src/components/animate';
import { CustomPopover, usePopover } from 'src/components/custom-popover';
import { pxToRem } from 'src/theme/typography';
import { useNavigate } from 'src/hooks/use-navigate';

// ----------------------------------------------------------------------

export function AccountPopover() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const popover = usePopover();
  const { t } = useTranslation('', { keyPrefix: 'account-popover' });
  const { t: tHeader } = useTranslation('', { keyPrefix: 'header' });

  const handleLogout = () => {
    logout();
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    navigate(path);
  };

  const menuOptions = useMemo(
    () => [
      {
        label: t('home'),
        linkTo: '/',
      },
      // {
      //   label: t('settings'),
      //   linkTo: PATHS.app.settings,
      // },
    ],
    [t]
  );

  const displayName = `${user?.first_name || ''} ${user?.last_name || ''}`;

  return (
    <>
      <IconButton
        data-testid="header-account"
        aria-label={tHeader('account')}
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={user?.photo}
          alt={displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {displayName?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 230, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle1" noWrap>
            {displayName}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', fontSize: pxToRem(15) }}
            noWrap
          >
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {menuOptions.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          {t('logout')}
        </MenuItem>
      </CustomPopover>
    </>
  );
}
